'use strict';

const CITIES_API = '/api/cities.json';

class Utils {
    /*@ngInject*/
    constructor(inventory, iconCache, $http, $state, $q, $mdDialog) {
        _.extend(this, {
            inventory: inventory,
            getIconFor: iconCache,
            $http: $http,
            $state: $state,
            $q: $q,
            $mdDialog: $mdDialog
        });
    }

    pluckAll(obj, keysToPick) {
        return _.partialRight(_.pick, keysToPick)(obj);
    }

    getRootNameOf(item, matchingMinimumHierarchy, startFromRight) {
        let itemQualifiedName = item.qualifiedName || this.inventory.getQualifiedNameFor(item);

        if (!itemQualifiedName) {
            return null;
        }

        let rootName = itemQualifiedName.split('.');
        return (
            startFromRight ?
                rootName.slice(-matchingMinimumHierarchy) :
                rootName.slice(0, matchingMinimumHierarchy)
        ).join('.');
    }

    getItemColumn(withRootCategoryName, children, matchingMinimumHierarchy) {
        return _.extend(
            this.withDescriptionOf({qualifiedName: withRootCategoryName}, matchingMinimumHierarchy || 1),
            {children: children}
        );
    }

    withDescriptionOf(item, matchingMinimumHierarchy) {
        let qualifiedName = this.getRootNameOf(item, matchingMinimumHierarchy);

        if (!qualifiedName) {
            return item;
        }

        let basicDescription = this.inventory.getItemWithQualifiedName(qualifiedName);
        return _.extend(item, basicDescription, this.getIconFor(qualifiedName));
    }

    withCountsOf(allItems, inColumn, matchingMinimumHierarchy) {
        let columnWithCounts = {};

        _(allItems)
            .groupBy(item => this.getRootNameOf(item, matchingMinimumHierarchy))
            .map((matchingItems, categoryName) => {
                return {
                    categoryName: categoryName,
                    size: _.filter(matchingItems, (item) => _.isUndefined(item._destroy)).length
                };
            })
            .each((category, size) => columnWithCounts[category.categoryName] = {size: category.size})
            .value();

        return _.extend(inColumn, {counts: {}}, {counts: columnWithCounts});
    }

    withAllUniqueItemsFromRootOf(category, matchingMaximumHierarchy) {
        let rootCategoryName = this.getRootNameOf(category, matchingMaximumHierarchy),
            allItemsInRootCategory = this.inventory.getChildrenFor(rootCategoryName);

        let uniqueItems = _(allItemsInRootCategory)
            .groupBy(item => this.getRootNameOf(item, matchingMaximumHierarchy))
            .map((allMatchingItems, categoryNameAtRequiredHierarchy) =>
                this.withDescriptionOf({qualifiedName: categoryNameAtRequiredHierarchy}, matchingMaximumHierarchy))
            .sortBy(item => item.score)
            .value();

        return _.extend(category, {uniqueChildren: uniqueItems});
    }

    /**
     * Note: mutates @param inItemColumns
     * @param column
     * @param inItemColumns
     * @param matchingMaximumHierarchy
     * @returns {*}
     */
    updateCountsOf(column, inItemColumns, matchingMaximumHierarchy) {
        let indexOfColumn = _.findIndex(inItemColumns, {qualifiedName: column.qualifiedName});
        if (indexOfColumn < -1) {
            throw new Error('How did this column come here?', column, inItemColumns);
        }

        let updatedColumn = this.withCountsOf(column.children, column, matchingMaximumHierarchy);
        inItemColumns.splice(indexOfColumn, 1, updatedColumn);

        return inItemColumns;
    }

    compareQualifiedNameOf(item, withName, withMinimumHierarchy) {
        let rootNameAtMinimumHierarchy = this.getRootNameOf(item, withMinimumHierarchy);
        return rootNameAtMinimumHierarchy === withName;
    }

    isInPrimaryCategory(item) {
        let primaryRoots = {furniture: 1, electronics: 1, vehicle: 1};
        let qualifiedRootName = this.getRootNameOf(item, 1);
        return qualifiedRootName in primaryRoots;
    }

    /**
     * This will return a NEW array of flat order item(s) attributes,
     *   depending on the level of specificity available
     * @return [order item(s) attributes including vehicle order details]
     */
    flattenOrderItems(orderItems, itemStatistics, vehicleStatistics, orderPropertyType) {
        // If no attributes specified, returns an empty array
        if (_.isEmpty(orderItems)) {
            return [];
        }

        return _(orderItems)
            .map((orderItem) => {
                let orderItemId = orderItem.id,
                    majorCategoryId = orderItem.item_id,
                    numberOfItemsInMajorCategory = orderItem.quantity;

                let orderItemDetails = this.extendWithIdentifierAndItemId(orderItem.order_item_details, itemStatistics, majorCategoryId);
                let vehicleOrderDetails = this.extendWithIdentifierAndItemId(orderItem.vehicle_order_details, vehicleStatistics, majorCategoryId);
                let bikesAndCycles = this.getBikesAndCyclesFrom(vehicleOrderDetails);
                let cars = this.getCarsFrom(vehicleOrderDetails);
                let vehicles = cars.concat(bikesAndCycles);
                let items = orderItemDetails;

                /**
                 * If property type is specified,
                 *  1. If property type is vehicle
                 *      all items in vehicle order details will bne treated as vehicles
                 *  2. Else -
                 *      1. bikes and cycles will be treated as items
                 *      2. cars will still be treated as a vehicle
                 */

                let flatItems = [];

                if (items.length) {
                    let flatOrderItems = _(items)
                        .map((itemDetails) => {
                            let range = _.range(itemDetails.quantity);
                            return _.map(range, () => _.extend(_.clone(itemDetails, true), {quantity: 1}));
                        })
                        .flatten()
                        .value();
                    flatItems = flatItems.concat(flatOrderItems);
                }

                if (vehicles.length) {
                    let flatVehicles = _(vehicles)
                        .map((vehicleDetails) => {
                            let range = _.range(vehicleDetails.quantity);
                            return _.map(range, () =>  _.extend(_.clone(vehicleDetails, true), {quantity: 1}));
                        })
                        .flatten()
                        .value();
                    flatItems = flatItems.concat(flatVehicles);
                }

                // If the number of fully detailed flat items are less than
                //  the total specified quantity,
                //  fill the rest with the root category
                while (flatItems.length < numberOfItemsInMajorCategory) {
                    flatItems.push({
                        order_item_id: orderItemId,
                        item_id: majorCategoryId
                    });
                }

                return flatItems;
            })
            .flatten()
            .value();

    }

    mergeOrderItems(orderItems, orderPropertyType) {
        // If no attributes specified, returns an empty object
        if (_.isEmpty(orderItems)) {
            return {};
        }

        return _(orderItems)
            .groupBy('item_id')
            .map((children, categoryId) => {
                // Store the order item info before removals
                let firstItem = children[0];
                const orderItemId = firstItem.order_item_id,
                    orderItemQualifiedName = firstItem.qualifiedName;

                // Handle destroyed items
                let itemsToRemove = _.filter(children, (item) => item._destroy);
                if (itemsToRemove.length) {
                    _.forEach(itemsToRemove, (itemToRemove) => {
                        const allSimilarItemsInChildren = _.filter(children, (flatItem) =>
                            flatItem.id === itemToRemove.id
                        );
                        /**
                         * If the number of similar items >= 1
                         *  remove ONE arbitrary item from flat order items
                         * If the number of similar items === 1
                         *  mark the item as destroyed
                         */
                        const indexOfItemInChildren = _.indexOf(children, itemToRemove);
                        if (allSimilarItemsInChildren.length > 1) {
                            if (indexOfItemInChildren !== -1) {
                                children.splice(indexOfItemInChildren, 1);
                            }
                        }
                        if (allSimilarItemsInChildren.length === 1) {
                            const itemFromChildren = children[indexOfItemInChildren];
                            if (itemFromChildren.id) {
                                // This item has been persisted to DB
                                // Since all items of this type have been removed
                                // Mark this item for removal
                                children[indexOfItemInChildren]._destroy = true;
                            } else {
                                // This item has not been persisted to DB
                                // Remove this item from memory
                                children.splice(indexOfItemInChildren, 1);
                            }
                        }
                    });
                }

                // Calculate the total number of order item details NOT marked for destruction
                const liveChildren = _.filter(children, (item) => _.isUndefined(item._destroy));

                let orderItem = {
                    id: orderItemId,
                    item_id: parseInt(categoryId, 10),
                    qualifiedName: this.getRootNameOf({qualifiedName: orderItemQualifiedName}, 2),
                    quantity: _.sum(liveChildren, (item) => item.quantity || 1)
                };

                let detailedItems = _.filter(children , (child) => !_.isUndefined(child.identifier)),
                    detailedHouseholdItems = this.getHouseholdItemsFrom(detailedItems),
                    detailedCars = this.getCarsFrom(detailedItems),
                    detailedBikesAndCycles = this.getBikesAndCyclesFrom(detailedItems);

                let allHouseholdItems = detailedHouseholdItems,
                    allVehicles = detailedCars.concat(detailedBikesAndCycles);

                if (allHouseholdItems.length) {
                    orderItem. order_item_details = this.mergeOrderItemDetails(allHouseholdItems);
                }

                if (allVehicles.length) {
                    orderItem.vehicle_order_details = this.mergeOrderItemDetails(allVehicles);
                }

                /**
                 * If the order item has been saved,
                 *  and there are no more children left in it,
                 *  mark the entire item to be destroyed
                 */
                if (liveChildren.length === 0 && orderItem.id) {
                    orderItem._destroy = true;
                } else {
                    delete orderItem._destroy;
                }

                return orderItem;
            })
            .groupBy((item, index) => index)
            .reduce((acc, item, index) => {
                acc[index] = item[0];
                return acc;
            }, {});
    }

    mergeOrderItemDetails(orderItemDetails) {
        // If no attributes specified, returns an empty array
        if (_.isEmpty(orderItemDetails)) {
            return {};
        }

        return _(orderItemDetails)
            .map((orderItemDetail) => _.extend(orderItemDetail, {
                is_include: _.isUndefined(orderItemDetail.is_include) ? true : orderItemDetail.is_include
            }))
            .groupBy('identifier')
            .map((children, itemId) => _.extend(children[0], {quantity: children.length}))
            .groupBy((item, index) => index)
            .reduce((acc, item, index) => {
                acc[index] = item[0];
                return acc;
            }, {});
    }

    extendWithIdentifierAndItemId(items, statistics, majorCategoryIdFromMethod) {
        return _.map(items, (item) => {
            let statisticId = item.vehicle_statistic_id || item.item_statistic_id,
                itemFromStatistics = _.find(statistics, {id: statisticId});

            let identifier = item.identifier || (itemFromStatistics && itemFromStatistics.identifier),
                itemId = item.item_id || (itemFromStatistics && itemFromStatistics.item_id);

            if (!identifier || !itemId) {
                throw new Error("Shit hit the fan");
                return item;
            }

            return _.extend({},
                item,
                {
                    identifier: identifier,
                    item_id: itemId
                }
            );
        })
    }

    getHouseholdItemsFrom(items) {
        return _(items)
            .filter((item) => {
                const itemQualifiedName = this.inventory.getQualifiedNameFor(item);
                return !_.contains(itemQualifiedName, 'cars')
                    && !_.contains(itemQualifiedName, 'bikes')
                    && !_.contains(itemQualifiedName, 'cycles')
            })
            .value();
    }

    getBikesAndCyclesFrom(items) {
        return _(items)
            .filter((item) => {
                const itemQualifiedName = this.inventory.getQualifiedNameFor(item);
                return _.contains(itemQualifiedName, 'bikes') || _.contains(itemQualifiedName, 'cycles')
            })
            .value();
    }

    getCarsFrom(items) {
        return _(items)
            .filter((item) => {
                const itemQualifiedName = this.inventory.getQualifiedNameFor(item);
                return _.contains(itemQualifiedName, 'cars');
            })
            .value();
    }

    fetchAllCities () {
        return this.$http({
            method: 'GET',
            url: CITIES_API,
            headers: "application/json; charset=utf-8"
        }).then(
            (response) => {
                return response.data;
            },
            (error) => this.$q.reject({
                error: {
                    code: this.constants.NO_CITIES_AVAILABLE,
                    response: error
                }
            })
        );
    };

    defaultValuationOf(propertyType, orderItems) {
        const BHK = /BHK/ig;
        const valuation = {
            "1 BHK": 60000,
            "2 BHK": 80000,
            "3 BHK": 100000,
            "3+ BHK": 100000,
            "FEW_ITEMS": 15000,
            "DUPLEX_VILLA": 150000
        };

        if (BHK.test(propertyType) || propertyType === 'FEW_ITEMS' || propertyType === 'DUPLEX_VILLA') {
            return valuation[propertyType];
        } 

        const itemValuationFactor = 0.6;
        return this.totalValuationOf(orderItems) * itemValuationFactor;
    }

    totalValuationOf(orderItems) {
        return _(orderItems)
            .pluck('order_item_details')
            .map((items)=> _.values(items))
            .flatten()
            .sum((item) => item.quantity * item.valuation);
    }

    busyDeferred(instance, deferred, busyPropertyName) {
        instance[busyPropertyName] = true;
        return deferred
            .then(
                () => instance[busyPropertyName] = false,
                () => instance[busyPropertyName] = false
            );
    }

    /* ordinal suffix for numbers */
    ordinalSuffixOf(i) {
        let j = i % 10,
            k = i % 100;
        if (j == 1 && k != 11) {
            return i + "st";
        }
        if (j == 2 && k != 12) {
            return i + "nd";
        }
        if (j == 3 && k != 13) {
            return i + "rd";
        }
        return i + "th";
    }

    getQuotePageForOrderWith(moveType, propertyType) {
        const INTERCITY = 'intercity', LOCAL = 'local';
        const BHK = /BHK/ig, ITEM = /item/ig, VEHICLE = /vehicle/ig;

        let quoteType;

        if (VEHICLE.test(propertyType)) {
            quoteType = 'base.quotesVehicle';
        }
        else if (moveType === LOCAL) {
            if (BHK.test(propertyType) || ITEM.test(propertyType)) {
                quoteType = 'base.quotes';
            } else {
                quoteType = 'base.quotes';
            }
        } else if (moveType === INTERCITY) {
            if (BHK.test(propertyType) || ITEM.test(propertyType)) {
                quoteType = 'base.quotesInterCity';
            } else if (VEHICLE.test(propertyType)) {
                quoteType = 'base.quotesVehicle';
            }
        }

        return quoteType;
    }

    /**
     * Find an equivalent item in given set of items, matching given item
     */
    findItemInOrderItems(toFind, orderItems) {
        const orderItemDetails = _(orderItems)
            .pluck('order_item_details')
            .map(item => _.toArray(item))
            .flatten()
            .value();
        const itemStatisticIdIdToMatch = toFind.item_statistic_id;
        return _.find(orderItemDetails, (item) => item.item_statistic_id === itemStatisticIdIdToMatch);
    }

    /* display numbers in thousands(k), lakhs(l), crores (Cr) */
    monetize(value) {
        let format = [
            { divisor: 1000, suffix: 'k' },
            { divisor: 100000, suffix: 'l' },
            { divisor: 10000000, suffix: 'Cr' }
        ],
        returnVal = '₹ '+value;

        format.some((f) => {
            if(value/f.divisor >= 1 && value/f.divisor < 100) {
                returnVal = '₹ '+ _.ceil(value/f.divisor, 2) + f.suffix;

            }
        });
        return returnVal;
    }

    /*get order number from order id*/
    getOrderNo(orderId) {
        let prefix = "BLL",
            orderNo = orderId.toString();

        while(orderNo.length < 6) orderNo = '0' + orderNo;

        return (prefix + orderNo);
    }

    /* Check if DOM element is in viewport */
    isElementInViewport(el) {
        var rect = el.getBoundingClientRect();

        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    sortByIndexInArray(value, indexProvider) {
        const indexInProvider = _.indexOf(indexProvider, value);
        return indexInProvider;
    }

    sortMajorCategories(category) {
        const categoryPriorities = ['furniture', 'electronics', 'others', 'vehicles', 'vehicle'];
        return this.sortByIndexInArray(_.isString(category) ? category : category.name, categoryPriorities);
    }

    getAdminStateIfApplicable(forState) {
        const currentState = this.$state.current.name;
        const isAdminPath = _.contains(currentState, 'admin');

        return isAdminPath ? forState + "-admin" : forState;
    }

    getDefaultQuoteDateAsSeconds(moveDateInSeconds, quote) {
        const moveDate = moment(new Date(moveDateInSeconds * 1000));
        const today = moment().startOf('day');

        let adjustedMoveDate = moveDate;
        /**
         * If the move date has passed
         *  select a quote for the next day
         */
        if (moveDate < today) {
            adjustedMoveDate = today.add(1, 'day');
        }

        /**
         * If adjusted date is not present in the list of surge dates
         *  select the first surge date available
         */
        if (!this.hasSurgeForDate(adjustedMoveDate, quote)) {
            adjustedMoveDate = this.earliestDateWithSurge(quote);
        }

        const adjustedMoveDateInSeconds = adjustedMoveDate.toDate().getTime() / 1000;

        return adjustedMoveDateInSeconds;
    }

    hasSurgeForDate(dateAsMoment, quote) {
        const surgeDates = _(quote.surges).pluck('surge_date').flatten().value();
        const surgeDateFormat = 'YYYY-MM-DD';
        const dateWithFormat = dateAsMoment.format(surgeDateFormat);

        return _.contains(surgeDates, dateWithFormat);
    }

    earliestDateWithSurge(quote) {
        const surgeDates = _(quote.surges)
            .pluck('surge_date')
            .flatten()
            .map(surgeDate => moment(surgeDate).toDate().getTime())
            .sort()
            .value();

        return moment(surgeDates[0]);
    }

    getRootDisplayNameForItem(item) {
        const rootItem = this.withDescriptionOf({qualifiedName: this.getRootNameOf(item, 2)});
        return rootItem.displayName.toLowerCase();
    }

    getMinMoveDate() {
        let minMoveDate = moment().subtract(1, 'days').toDate(),
            formattedMinMoveDate = moment(this.minMoveDate).format('YYYY-DD-MM');
        return [minMoveDate, formattedMinMoveDate];
    }

    getMaxMoveDate() {
        let maxMoveDate = moment().add(6, 'months').toDate(),
            formattedMaxMoveDate = moment(this.maxMoveDate).format('YYYY-DD-MM');
        return [maxMoveDate, formattedMaxMoveDate];
    }

    getDefaultMoveDate() {
        return moment().add(5, 'days').toDate();
    }

    getCurrentDateInSeconds() {
        return parseInt(moment().toDate().getTime() / 1000, 10);
    }

    constants = {
        NO_CITIES_AVAILABLE: 'NO_CITIES_AVAILABLE'
    }

    showAlertPopup(title, label, content) {
        return this.$mdDialog.show({
                controller: require('common/popup.component/popup.controller.js'),
                controllerAs: 'popup',
                template: require('common/popup.component/popup.html'),
                locals: {
                    title: title,
                    content: content,
                    label: label
                },
                parent: angular.element(document.body),
                clickOutsideToClose: true
            })
    }

    showErrorPopup(title, label, content) {
        return this.$mdDialog.show({
            controller: require('common/popup.component/popup.controller.js'),
            controllerAs: 'popup',
            template: require('common/popup.component/error.popup.html'),
            locals: {
                title: title,
                content: content,
                label: label
            },
            parent: angular.element(document.body),
            clickOutsideToClose: true
        })
    }

    //capitalize the first character of string
    capitalize(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    getURLParameterByName(name, url) {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, "\\$&");
    var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
        results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, " "));
}

}

export default Utils;


// WEBPACK FOOTER //
// ./client/app/common/utils/utils.js